<template>
  <div>
    <record_loader v-if="loading" />
    <div v-else>
      <div class="primary-container">
        <h1 class="red_color">Submit Timesheets</h1>
        <div v-if="server_error" class="vvERROR">
          Could not load submission periods. Please try again.
        </div>
        <div v-else class="primary-container">

          <!-- --- Unsubmitted / In-Progress --- -->
          <div class="col-12 mb-4">
            <h4 class="text-primary mb-3">In Progress / Unsubmitted Periods</h4>
            <ValidationObserver ref="unsubmittedForm" slim>
              <form class="row" role="form" @submit.prevent="enter_new_ts(selectedUnsubmitted)">
                <ValidationProvider v-slot="{ errors }" rules="required">
                  <div class="form-group select_period_form_inputs">
                    <select
                        v-model="selectedUnsubmitted"
                        class="form-control form-control-lg"
                        :disabled="no_dates || unsubmittedWeeks.length === 0"
                    >
                      <option v-for="date in unsubmittedWeeks" :key="'u-' + date.start_date" :value="date">
                        {{ dateRange(date.start_date, date.end_date) }}
                        (In Progress)
                      </option>
                      <option v-if="unsubmittedWeeks.length === 0" disabled :value="null">
                        No in-progress periods available
                      </option>
                    </select>
                    <button
                        class="btn orcaBtn"
                        type="submit"
                        :disabled="no_dates || !selectedUnsubmitted"
                    >
                      Select Period
                    </button>
                  </div>
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
              </form>
            </ValidationObserver>
          </div>

          <!-- --- Submitted --- -->
          <div class="col-12">
            <h4 class="text-success mb-3">Submitted Periods</h4>
            <ValidationObserver ref="submittedForm" slim>
              <form class="row" role="form" @submit.prevent="enter_new_ts(selectedSubmitted)">
                <ValidationProvider v-slot="{ errors }" rules="required">
                  <div class="form-group select_period_form_inputs">
                    <select
                        v-model="selectedSubmitted"
                        class="form-control form-control-lg"
                        :disabled="no_dates || submittedWeeks.length === 0"
                    >
                      <option v-for="date in submittedWeeks" :key="'s-' + date.start_date" :value="date">
                        {{ dateRange(date.start_date, date.end_date) }}
                        (Submitted)
                      </option>
                      <option v-if="submittedWeeks.length === 0" disabled :value="null">
                        No submitted periods available
                      </option>
                    </select>
                    <button
                        class="btn orcaBtn"
                        type="submit"
                        :disabled="no_dates || !selectedSubmitted"
                    >
                      View Period
                    </button>
                  </div>
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
              </form>
            </ValidationObserver>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import record_loader from "loaders/order_loader.vue";

export default {
  name: "client_timesheet_select_period",
  components: {
    record_loader,
  },
  data() {
    return {
      loading: false,
      submissionWeeks: [],
      unsubmittedWeeks: [],
      submittedWeeks: [],
      selectedUnsubmitted: null,
      selectedSubmitted: null,
      company_id: null,
      company_name: null,
      timesheet_cycle_id: null,
      timesheet_template_name: null,
      no_dates: false,
      server_error: false,
    };
  },
  async beforeMount() {
    await this.getData();
  },
  methods: {
    dateFormat(date) {
      return this.$moment.utc(date).format("DD/MM/YYYY");
    },
    dateRange(d1, d2) {
      return this.$moment.utc(d1).format("DD/MM/YYYY") + " - " + this.$moment.utc(d2).format("DD/MM/YYYY");
    },
    enter_new_ts(selectedPeriod) {
      if (!selectedPeriod) return;

      this.$router.push({
        name: "c_timesheet_r2_select_sites",
        params: {
          company_id: this.company_id,
          end_date: selectedPeriod.end_date,
          start_date: selectedPeriod.start_date,
        },
      });
    },
    async getData() {
      this.loading = true;
      this.server_error = false;
      this.no_dates = false;

      try {
        const company = await axios.get(
            this.crmApiPath + "contact/" + this.$store.state.auth.me.contact_id + "/company/show"
        );

        this.timesheet_template_name = company.data.timesheet_template_name;
        this.company_id = company.data.id;
        this.company_name = company.data.company_name;
        this.timesheet_cycle_id = company.data.rel_payment_process_info
            ? company.data.rel_payment_process_info.timesheet_cycle_id
            : null;

        const result = await axios.get(
            `${this.crmApiPath}r3/client/timesheets/company/${this.company_id}/submission_weeks?limit=all`
        );

        if (result.status !== 200) {
          this.server_error = true;
          return;
        }

        const allWeeks = result.data.weeks || [];

        this.submissionWeeks = allWeeks;
        this.unsubmittedWeeks = allWeeks.filter(w => w.submitted === "no");
        this.submittedWeeks = allWeeks.filter(w => w.submitted === "yes");
        this.no_dates = allWeeks.length === 0;

        this.selectedUnsubmitted = this.unsubmittedWeeks.length > 0 ? this.unsubmittedWeeks[0] : null;
        this.selectedSubmitted = this.submittedWeeks.length > 0 ? this.submittedWeeks[0] : null;
      } catch (e) {
        console.error("Error loading submission weeks", e);
        this.server_error = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
div.primary-container {
  display: grid;
  justify-content: flex-start;
  align-content: flex-start;
  width: 100%;
  height: 100%;
  overflow: auto;
}

button.orcaBtn {
  width: auto;
  max-width: fit-content;
}

div.select_period_form_inputs {
  display: grid;
  grid-template-columns: 100%;
  align-content: flex-start;
  justify-content: flex-start;
  grid-gap: 10px;
}

h4 {
  font-weight: bold;
  font-size: 1.1rem;
}
</style>
